import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { FindParams, HttpTypes, SelectParams } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";

export class Customer {
  /**
   * @ignore
   */
  private client: Client;
  /**
   * @ignore
   */
  constructor(client: Client) {
    this.client = client;
  }

  /**
   * This method registers a customer. It sends a request to the [Register Customer](https://docs.medusajs.com/api/store#customers_postcustomers)
   * API route.
   *
   * You must use the {@link Auth.register} method first to retrieve a registration token. Then, pass that
   * registration token in the `headers` parameter of this method as an authorization bearer header.
   *
   * Related guide: [How to register customer in storefront](https://docs.medusajs.com/resources/storefront-development/customers/register)
   *
   * @deprecated This method is deprecated and will throw an error when called.
   * @param body - The customer's details.
   * @param query - Configure the fields to retrieve in the customer.
   * @param headers - Headers to pass in the request. This is where you include the authorization JWT registration token.
   * @returns The customer's details.
   *
   * @example
   * const token = await sdk.auth.register("customer", "emailpass", {
   *   "email": "customer@gmail.com",
   *   "password": "supersecret"
   * })
   *
   * sdk.store.customer.create(
   *   {
   *     "email": "customer@gmail.com"
   *   }
   *   {}
   *   {
   *     Authorization: `Bearer ${token}`
   *   }
   * )
   * .then(({ customer }) => {
   *   console.log(customer)
   * })
   */
  create = (
    _body: HttpTypes.StoreCreateCustomer,
    _query?: SelectParams,
    _headers?: ClientHeaders
  ): Promise<never> =>
    Promise.reject(
      new Error(
        "sdk.store.customer.create() is deprecated and no longer available"
      )
    );
  /**
   * This method updates the logged-in customer's details. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the
   * [Update Customer](https://docs.medusajs.com/api/store#customers_postcustomersme) API route.
   *
   * Related guide: [How to edit customer's profile in the storefront](https://docs.medusajs.com/resources/storefront-development/customers/profile).
   *
   * @param body - The customer's details to update.
   * @param query - Configure the fields to retrieve in the customer.
   * @param headers - Headers to pass in the request.
   * @returns The customer's details.
   *
   * @example
   * sdk.store.customer.update({
   *   first_name: "John"
   * })
   * .then(({ customer }) => {
   *   console.log(customer)
   * })
   */
  update = async (
    body: HttpTypes.StoreUpdateCustomer,
    query?: SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCustomerResponse>(
      `/store/customers/me`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method retrieves the logged-in customer's details. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the [Get Logged-In Customer](https://docs.medusajs.com/api/store#customers_getcustomersme)
   * API route.
   *
   * @deprecated - Use sdk.store.customer.profile()
   * @param query - Configure the fields to retrieve in the customer.
   * @param headers - Headers to pass in the request.
   * @returns The customer's details.
   *
   * @example
   * sdk.store.customer.retrieve()
   * .then(({ customer }) => {
   *   console.log(customer)
   * })
   */
  retrieve = async (query?: SelectParams, headers?: ClientHeaders) => {
    return this.client.fetch<HttpTypes.StoreCustomerResponse>(
      `/store/profile`,
      {
        query,
        headers
      }
    );
  };
  /**
   * This method retrieves the logged-in customer's profile. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the /store/profile
   * API route.
   *
   * @param headers - Headers to pass in the request.
   * @returns The customer's details.
   *
   * @example
   * sdk.store.customer.profile()
   * .then(({ customer }) => {
   *   console.log(customer)
   * })
   */
  profile = async (headers?: ClientHeaders) => {
    return this.client.fetch<SalonStockerHttpTypes.StoreProfileResponse>(
      `/store/profile`,
      {
        headers
      }
    );
  };
  /**
   * Sets the logged-in salon's password.
   *
   * Runs against the current session rather than Medusa's reset-token
   * round-trip - that would email them, and the whole point of this flow is
   * that the welcome email waits until the salon has chosen to come back.
   *
   * The response's `is_first_password` distinguishes activation (which sends
   * the welcome email) from a routine change (which sends nothing).
   *
   * @param password - The new password.
   * @param headers - Headers to pass in the request.
   */
  setPassword = async (password: string, headers?: ClientHeaders) => {
    return this.client.fetch<{ is_first_password: boolean }>(
      `/store/profile/password`,
      {
        method: "POST",
        body: { password },
        headers
      }
    );
  };
  /**
   * This method creates an address for the logged-in customer. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the [Create Address](https://docs.medusajs.com/api/store#customers_postcustomersmeaddresses)
   * API route.
   *
   * Related guides: [How to manage customer's addresses in the storefront](https://docs.medusajs.com/resources/storefront-development/customers/addresses)
   *
   * @param body - The address's details.
   * @param query - Configure the fields to retrieve in the customer.
   * @param headers - Headers to pass in the request.
   * @returns The customer's details.
   *
   * @example
   * sdk.store.customer.createAddress({
   *   country_code: "us"
   * })
   * .then(({ customer }) => {
   *   console.log(customer)
   * })
   */
  createAddress = async (
    body: HttpTypes.StoreCreateCustomerAddress,
    query?: SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCustomerResponse>(
      `/store/customers/me/addresses`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method updates the address of the logged-in customer. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the [Update Address](https://docs.medusajs.com/api/store#customers_postcustomersmeaddressesaddress_id)
   * API route.
   *
   * Related guides: [How to manage customer's addresses in the storefront](https://docs.medusajs.com/resources/storefront-development/customers/addresses)
   *
   * @param addressId - The ID of the address to update.
   * @param body - The details to update in the address.
   * @param query - Configure the fields to retrieve in the customer.
   * @param headers - Headers to pass in the request.
   * @returns The customer's details.
   *
   * @example
   * sdk.store.customer.updateAddress(
   *   "caddr_123",
   *   {
   *     country_code: "us"
   *   }
   * )
   * .then(({ customer }) => {
   *   console.log(customer)
   * })
   */
  updateAddress = async (
    addressId: string,
    body: HttpTypes.StoreUpdateCustomerAddress,
    query?: SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCustomerResponse>(
      `/store/customers/me/addresses/${addressId}`,
      {
        method: "POST",
        headers,
        body,
        query
      }
    );
  };
  /**
   * This method retrieves the logged-in customer's address. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the [List Customer's Address](https://docs.medusajs.com/api/store#customers_getcustomersmeaddresses)
   * API route.
   *
   * Related guides: [How to manage customer's addresses in the storefront](https://docs.medusajs.com/resources/storefront-development/customers/addresses)
   *
   * @param query - Configure the fields to retrieve in the addresses.
   * @param headers - Headers to pass in the request.
   * @returns The paginated list of addresses.
   *
   * @example
   * To retrieve the list of addresses:
   *
   * ```ts
   * sdk.store.customer.listAddress()
   * .then(({ addresses, count, offset, limit }) => {
   *   console.log(addresses)
   * })
   * ```
   *
   * To configure the pagination, pass the `limit` and `offset` query parameters.
   *
   * For example, to retrieve only 10 items and skip 10 items:
   *
   * ```ts
   * sdk.store.customer.listAddress({
   *   limit: 10,
   *   offset: 10
   * })
   * .then(({ addresses, count, offset, limit }) => {
   *   console.log(addresses)
   * })
   * ```
   *
   * Using the `fields` query parameter, you can specify the fields and relations to retrieve
   * in each address:
   *
   * ```ts
   * sdk.store.customer.listAddress({
   *   fields: "id,country_code"
   * })
   * .then(({ addresses, count, offset, limit }) => {
   *   console.log(addresses)
   * })
   * ```
   *
   * Learn more about the `fields` property in the [API reference](https://docs.medusajs.com/api/store#select-fields-and-relations).
   */
  listAddress = async (
    query?: FindParams & HttpTypes.StoreCustomerAddressFilters,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCustomerAddressListResponse>(
      `/store/customers/me/addresses`,
      {
        query,
        headers
      }
    );
  };
  /**
   * This method retrieves an address of the logged-in customer. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the [Get Address](https://docs.medusajs.com/api/store#customers_getcustomersmeaddressesaddress_id)
   * API route.
   *
   * Related guides: [How to manage customer's addresses in the storefront](https://docs.medusajs.com/resources/storefront-development/customers/addresses)
   *
   * @param addressId - The address's ID.
   * @param query - Configure the fields to retrieve in the address.
   * @param headers - Headers to pass in the request.
   * @returns The address's details.
   *
   * @example
   * To retrieve an address by its ID:
   *
   * ```ts
   * sdk.store.customer.retrieveAddress(
   *   "caddr_123"
   * )
   * .then(({ address }) => {
   *   console.log(address)
   * })
   * ```
   *
   * To specify the fields and relations to retrieve:
   *
   * ```ts
   * sdk.store.customer.retrieveAddress(
   *   "caddr_123",
   *   {
   *     fields: "id,country_code"
   *   }
   * )
   * .then(({ address }) => {
   *   console.log(address)
   * })
   * ```
   *
   * Learn more about the `fields` property in the [API reference](https://docs.medusajs.com/api/store#select-fields-and-relations).
   */
  retrieveAddress = async (
    addressId: string,
    query?: SelectParams,
    headers?: ClientHeaders
  ) => {
    return this.client.fetch<HttpTypes.StoreCustomerAddressResponse>(
      `/store/customers/me/addresses/${addressId}`,
      {
        query,
        headers
      }
    );
  };
  /**
   * This method deletes an address of the logged-in customer. The customer must be logged in
   * first with the {@link Auth.login} method.
   *
   * It sends a request to the [Remove Address](https://docs.medusajs.com/api/store#customers_deletecustomersmeaddressesaddress_id)
   * API route.
   *
   * Related guides: [How to manage customer's addresses in the storefront](https://docs.medusajs.com/resources/storefront-development/customers/addresses)
   *
   * @param addressId - The address's ID.
   * @param headers - Headers to pass in the request.
   * @returns The deletion's details.
   *
   * @example
   * sdk.store.customer.deleteAddress("caddr_123")
   * .then(({ deleted, parent: customer }) => {
   *   console.log(customer)
   * })
   */
  deleteAddress = async (addressId: string, headers?: ClientHeaders) => {
    return this.client.fetch<HttpTypes.StoreCustomerAddressDeleteResponse>(
      `/store/customers/me/addresses/${addressId}`,
      {
        method: "DELETE",
        headers
      }
    );
  };
}
